import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import 'rxjs/add/operator/toPromise';
import { JobsDTO, Jobsfilter, SavedSearchFilter, SavedSearch, FilterData, BinResult } from './job-results.dto';

import { api } from '../../shared/api';
import { AuthenticationService } from '../authentication/authentication.service';
import * as _ from 'lodash';

@Injectable()
export class JobSearchService {
  private jobs: JobsDTO;
  private jobFilters: Jobsfilter[] = [];
  private Location: String;
  private savedSearchId: number;
  private filterData: FilterData = null;
  private totalCount = 0;
  private startRow = 0;
  private pageSize = 20;
  private pagedJobResults: BinResult[] = [];
  private homeLocSearch = false;
  private locationlist: any[] = [];
  private dateRangeList: any[] = [];

  constructor(
    private http: HttpClient,
    public authService: AuthenticationService
  ) {
    this.setDefaultFilters();
  }

  clearPagination() {
    if (this.filterData) {
      this.filterData.StartRow = 0;
    }
  }

  private isloggedin() {
    return this.authService.isUserLoggedin();
  }

  clearFilters() {
    this.jobFilters = [];
    this.setDefaultFilters();
    this.savedSearchId = null;
  }

  fetchPagedResults(pageNumber: number) {
    this.filterData.StartRow = pageNumber;
    return this.fetchJobs();
  }

  fetchJobs(location?: String, keywords?: String) {
    this.populateFilterData(location, keywords);

    let apiToFetchJobs;
    if (this.isloggedin()) {
      apiToFetchJobs = api.jobsearch.SEARCH_JOBS;
    } else {
      apiToFetchJobs = api.jobsearch.PUBLIC_SEARCH;
    }

    this.filterData.FacetLimit = -1;

    return this.http.post<JobsDTO>(apiToFetchJobs, this.filterData)
      .toPromise()
      .then(res => {
        if (res.searchResults.bins.length > 0) {
          this.pagedJobResults = res.searchResults.bins[0].resultItems.searchResults;
          this.jobs = res;
          this.totalCount = this.jobs.totalCount;
          this.pageSize = this.jobs.pageCount;
          return true;
        } else {
          this.pagedJobResults = null;
          this.jobs = res;
          this.totalCount = 0;
          this.pageSize = 20;
          return true;
        }
      })
      .catch(this.handleError);
  }

  fillInFilters(data, dates: Date[]) {
    this.jobFilters = [];
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < data[i].facetItems.length; j++) {
        const secObj = data[i].facetItems[j];
        this.pushToJobFilters(secObj);
      }
    }
    this.pushDateFilters(dates);
  }

  fillInSearch(location: String, keywords: String) {
    this.Location = location;
    this.filterData.SearchText = keywords;
  }

  findSearchText() {
    return this.filterData.KeywordSearchText;
  }
  fillInLocationFilter(data) {
    if (!this.jobFilters) {
      this.jobFilters = [];
    }
    if (data) {
      this.pushToJobFilters(data);
    }
  }

  fetchSavedSearches() {
    const httpParams = new HttpParams()
      .set('context', 'publicjob');

    return this.http.get<SavedSearchFilter[]>(api.jobsearch.LIST_SAVED_SEARCHES, { params: httpParams })
      .toPromise()
      .then(res => {
        const savedSearches: SavedSearchFilter[] = res;
        return savedSearches.filter(item => item.contexts === 'publicjob');
      })
      .catch(this.handleError);
  }

  fetchSortFields() {
    let apiToFetchSortFields;
    if (this.isloggedin()) {
      apiToFetchSortFields = api.jobsearch.GET_SORTABLE_FIELDS;
    } else {
      apiToFetchSortFields = api.jobsearch.PUBLIC_GET_SORTABLE_FIELDS;
    }

    const httpParams = new HttpParams()
      .set('context', 'publicjob');
    return this.http.get<any[]>(apiToFetchSortFields, { params: httpParams })
      .toPromise()
      .catch(this.handleError);
  }

  fetchNextFacetItems(category: String) {
    if (!category || category === '') {
      return null;
    }
    if (this.filterData == null) {
      this.setDefaultFilters();
    }
    const httpParams = new HttpParams()
      .set('searchTerm', this.filterData.SearchText.toString())
      .set('facetSearchTerm', '*')
      .set('facetType', 'facet_fields')
      .set('context', 'publicjob')
      .set('FacetFieldName', category.toString())
      .set('requestType', '10');

    let apiToFetchFacets;
    if (this.isloggedin()) {
      apiToFetchFacets = api.jobsearch.GET_SEARCH_FACETS;
    } else {
      apiToFetchFacets = api.jobsearch.PUBLIC_SEARCH_FACETS;
    }

    return this.http.get<JobsDTO>(apiToFetchFacets, { params: httpParams })
      .toPromise()
      .then(res => {
        const savedSearches: JobsDTO = res;
        return savedSearches.facets.length === 0 ? null : savedSearches.facets[0];
      })
      .catch(this.handleError);
  }

  getFilterFacets() {
    return this.jobs ? this.jobs.facets : [];
  }

  getPageSize() {
    return this.jobs.pageCount;
  }

  getJobResults() {
    if (this.jobs.searchResults.bins.length > 0) {
      return this.jobs.searchResults.bins[0];
    } else {
      return null;
    }
  }

  getPagedJobResults() {
    return this.pagedJobResults;
  }

  getSavedSearchId() {
    return this.savedSearchId;
  }

  getJobFilters() {
    return this.jobFilters;
  }

  removeFilterAt(index: number) {
    if (this.jobFilters.length > index) {
      this.jobFilters.splice(index, 1);
    }
  }

  getLocationFilters(facetName: String) {
    if (!this.jobs.facets || this.jobs.facets.length === 0) {
      return null;
    }
    const locations = this.jobs.facets.filter((facet) => facet.facetName === facetName);
    if (locations.length >= 0) {
      return locations[0];
    } else {
      return null;
    }
  }

  loadSavedSearch(id: number) {
    this.savedSearchId = id;

    const httpParams = new HttpParams()
      .set('searchId', id.toString());
    return this.http.get<SavedSearch>(api.jobsearch.LOAD_SAVED_SEARCH, { params: httpParams })
      .toPromise()
      .then(res => {
        const savedSearchData: SavedSearch = res;
        this.fillInSavedSearch(savedSearchData);
        return true;
      })
      .catch(this.handleError);
  }

  resetJobs() {
    return this.fetchJobs(this.Location, this.filterData.SearchText);
  }

  purgeFacetFilters(facetName: String) {
    this.jobFilters = this.jobFilters.filter((facet) => facet.searchFieldName !== facetName);
  }

  saveSearchFilters(name: string) {
    this.populateFilterData();

    const httpParams = new HttpParams()
      .set('name', name);
    return this.http.post(api.jobsearch.SAVE_SEARCH, this.filterData, { params: httpParams })
      .toPromise()
      .then(res => {
        return true;
      })
      .catch(this.handleError);
  }

  setSortOrder(sortObj: any) {
    if (this.filterData !== undefined && this.filterData !== null) {
    this.filterData.SortFilters = [{
      SortField: sortObj.sortFieldName,
      SortDirection: sortObj.sortDirection
    }];
  }
  }

  setHomeLocSearch(homeLocSearch) {
    this.homeLocSearch = homeLocSearch;
  }

  getHomeLocSearch() {
    return this.homeLocSearch;
  }

  getHomeLocFacets(facetName) {
    if (this.jobs === undefined) {
      this.fetchJobs().then(res => {
        const locations = this.jobs.facets.filter((facet) => facet.facetName === facetName);
        if (locations.length >= 0) {
          return locations[0];
        } else {
          return null;
        }
      });
    } else {
        const locations = this.jobs.facets.filter((facet) => facet.facetName === facetName);
        if (locations.length >= 0) {
          return locations[0];
        } else {
          return null;
        }
     }
  }

  clearLocationFilters() {
    this.jobFilters = null;
  }

    fetchLocations() {
    if (this.locationlist.length > 0) {
      return Promise.resolve(this.locationlist);
    }
    return this.http.get<any[]>( api.jobsearch.PUBLIC_GET_LOCATIONS)
    .toPromise()
    .then(res => {
      this.locationlist = res;
      return res;
    }).catch(this.handleError);
  }

  getNotSelectedLocations(locationlist) {
    if (this.jobFilters.length > 0) {
    locationlist.forEach(e => {
        const location = _.find(this.jobFilters, function (code) {
          return code.name === e.key;
        });
        if (location) {
          e.isApplied = true;
        } else {
          e.isApplied = false;
        }
  });
}
return  _.filter(locationlist, function (item) {
  return !item.isApplied
});
  }

  private fillInSavedSearch(data: SavedSearch) {
    this.filterData.SearchText = data.searchText;
    this.filterData.StartRow = data.startRow;
    this.filterData.Contexts = data.contexts;
    this.filterData.Filters = data.filters;
    this.jobFilters = data.filters;
    this.jobFilters.forEach(element => {
      element.uncodedValue = element.value;
    });
    this.filterData.SortFilters = data.sortFilters;
    return this.fetchJobs();
  }

  private hasDuplicateEntry(filter: any) {
    const results = this.jobFilters.filter(it => (it.name === filter.name &&
      it.uncodedValue === filter.value &&
      it.searchFieldName === filter.searchFieldName));
    return results.length > 0 ? true : false;
  }

  private handleError(error: any) {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }

  private setDefaultFilters() {
    this.filterData = {
      SearchText: '*',
      KeywordSearchText: '',
      StartRow: this.startRow,
      PageCount: this.pageSize,
      Contexts: ['publicjob'],
      Filters: [],
      SortFilters: [{ 'SortField': 'score', 'SortDirection': 'desc' }]

    };
  }

  private populateFilterData(location?: String, keywords?: String) {
    if (this.jobFilters[0] !== undefined && this.jobFilters[0].name === 'Distance') {
      this.filterData.Filters = this.jobFilters;
    } else if (this.filterData !== null) {
      this.filterData.Filters = this.jobFilters;
      this.filterData.SearchText =  keywords ?
      (keywords.indexOf('"') < 0 ? this.concatStrings(keywords.split(' '), ' AND ') : keywords) : '*';
      this.filterData.KeywordSearchText = keywords;
    }
    this.Location = (location ? location : '');
  }

 private concatStrings(inputString, concatChar) {
    let outputString = [];
    const format = /^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/;
    inputString.forEach(element => {
      if (!element.match(format) && element.toLowerCase() !== 'and' && element.toLowerCase() !== 'or') {
        outputString = outputString.concat(element.trim());
      }
    });
    return outputString.join(concatChar);
  }

  private pushDateFilters(dates: Date[]) {
    if (!dates || dates.length < 2) {
      return;
    }
    const fromDate = dates[0].toISOString().substr(0, 10);
    const toDate = dates[1].toISOString().substr(0, 10);
    const value = '[' + fromDate + ' TO ' + toDate + ']';
    const filter = {
      name: 'Posting Date',
      searchFieldName: 'Ext_PostingDate',
      toValue: toDate,
      fromValue: fromDate,
      value: encodeURI(value),
      uncodedValue: value
    };
    this.jobFilters.push(filter);
  }

  private pushToJobFilters(secObj) {
    if (secObj.isApplied) {
      if (this.hasDuplicateEntry(secObj.filterQuery)) { return; }
      const filter: Jobsfilter = new Jobsfilter;
      filter.name = secObj.filterQuery.name;
      filter.searchFieldName = secObj.filterQuery.searchFieldName;
      if (filter.searchFieldName === 'GeoLocation') {
        filter.value = encodeURI(secObj.filterQuery.value);
      } else {
        filter.value = encodeURIComponent(secObj.filterQuery.value);
      }

      filter.uncodedValue = secObj.filterQuery.uncodedValue === undefined ? secObj.filterQuery.value : secObj.filterQuery.uncodedValue ;
      this.jobFilters.push(filter);
    }
  }

  getJobCount() {
    return this.totalCount;
  }

  getDataByCategoryCode(catcd) {
    
    const catCds = [catcd]
    return this.http.post<any[]>( api.jobsearch.PUBLIC_GET_CODES, catCds)
    .toPromise()
    .then(res => {
      this.dateRangeList = res[0].codes;
      return res[0].codes;
    }).catch(this.handleError);
  }
}



// WEBPACK FOOTER //
// ../UI/src/app/shared/job-search/job-search.service.ts